import { default as Modal } from 'antd/es/modal';
import type { ReactNode } from 'react';
import { CountryFlag } from 'shared/src/CountryFlag';
import { CryptoFlag } from 'shared/src/CryptoFlag';
import { CryptoNetworkIcon } from 'shared/src/CryptoNetworkIcon';
import { getCountryCode } from 'shared/src/getCountryCode';

import { capitalizeFirstLetter } from 'utils/capitalize-first-letter';
import { formatHours } from 'utils/dayjs';
import { hasValue } from 'utils/hasValue';

import { CryptoAddress } from '@atoms/CryptoAddress/CryptoAddress';
import { FormattedDate } from '@atoms/FormattedDate/FormattedDate';

import { FormattedDateTime } from '@molecules/FormattedDateTime';

import {
  fallbackTransactionStatusColor,
  transactionStatusColor
} from './transactionStatusColor';
import type { CryptoTransaction } from './useCryptoTransactions';

import { getNetworkDisplayName } from '../config';
import { StatusBadge } from '../shared/StatusBadge';
import { shiftDecimal } from '../utils';

type Props = {
  transaction: CryptoTransaction;
  onClose: () => void;
};

type DetailRowProps = {
  label: string;
  children: ReactNode;
};

const DetailRow = ({ label, children }: DetailRowProps) => (
  <div className="flex flex-row justify-between gap-1">
    <span className="font-semibold text-content-secondary">{label}</span>
    <div className="text-right text-content-primary">{children}</div>
  </div>
);

const AddressValue = ({ address }: { address?: string | null }) => {
  if (!hasValue(address) || address.length === 0) {
    return <>-</>;
  }

  return <CryptoAddress address={address} />;
};

const StatusValue = ({ status }: { status?: string | null }) => {
  if (!hasValue(status)) {
    return <>-</>;
  }

  return (
    <StatusBadge
      statusText={status}
      color={transactionStatusColor[status] ?? fallbackTransactionStatusColor}
    />
  );
};

const InlineDateTime = ({ value }: { value: string }) => (
  <div className="flex w-[180px] flex-row justify-between text-base">
    <div className="font-light">
      <FormattedDate className="text-medium" value={value} />{' '}
    </div>
    {formatHours(value, 'cet')}
  </div>
);

const DepositDetails = ({
  transaction
}: {
  transaction: CryptoTransaction;
}) => {
  const displayDate = transaction.layer1_created_at ?? transaction.display_at;
  const amount =
    hasValue(transaction.formatted_amount) && hasValue(transaction.asset)
      ? `${transaction.formatted_amount} ${transaction.asset}`
      : '-';

  return (
    <>
      <div className="flex flex-col items-center gap-4">
        <p className="text-xl text-content-primary">
          Received {transaction.asset}
        </p>
        <div className="relative inline-flex">
          <CryptoFlag cryptoCode={transaction.asset ?? ''} className="size-8" />
          <CryptoNetworkIcon
            network={transaction.network ?? ''}
            className="absolute -bottom-0.5 -right-0.5 size-4"
          />
        </div>
        {hasValue(displayDate) ? (
          <FormattedDateTime value={displayDate} />
        ) : (
          <span>-</span>
        )}
      </div>

      <div className="flex flex-col gap-4 px-2 pb-4">
        <p className="text-base font-bold text-content-primary">
          Transaction details
        </p>

        <DetailRow label="Amount">{amount}</DetailRow>
        <DetailRow label="From address">
          <AddressValue address={transaction.from_address} />
        </DetailRow>
        <DetailRow label="To address">
          <AddressValue address={transaction.deposit_address} />
        </DetailRow>
        <DetailRow label="Transaction hash">
          <AddressValue address={transaction.tx_hash} />
        </DetailRow>
        <DetailRow label="Network">
          <span className="inline-flex items-center gap-2 text-sm">
            <CryptoNetworkIcon
              network={transaction.network ?? ''}
              className="size-5"
            />
            {hasValue(transaction.network)
              ? getNetworkDisplayName(transaction.network)
              : '-'}
          </span>
        </DetailRow>
        <DetailRow label="Status">
          <StatusValue status={transaction.status} />
        </DetailRow>
      </div>
    </>
  );
};

const WithdrawalDetails = ({
  transaction
}: {
  transaction: CryptoTransaction;
}) => {
  const displayDate = transaction.layer1_created_at ?? transaction.display_at;
  const amount =
    hasValue(transaction.formatted_amount) && hasValue(transaction.asset)
      ? `${transaction.formatted_amount} ${transaction.asset}`
      : '-';

  return (
    <>
      <div className="flex flex-col items-center gap-4">
        <p className="text-xl text-content-primary">
          Returned {transaction.asset}
        </p>
        <div className="relative inline-flex">
          <CryptoFlag cryptoCode={transaction.asset ?? ''} className="size-8" />
          <CryptoNetworkIcon
            network={transaction.network ?? ''}
            className="absolute -bottom-0.5 -right-0.5 size-4"
          />
        </div>
        {hasValue(displayDate) ? (
          <FormattedDateTime value={displayDate} />
        ) : (
          <span>-</span>
        )}
      </div>

      <div className="flex flex-col gap-4 px-2 pb-4">
        <p className="text-base font-bold text-content-primary">
          Transaction details
        </p>

        <DetailRow label="Amount">{amount}</DetailRow>
        <DetailRow label="To address">
          <AddressValue address={transaction.destination_address} />
        </DetailRow>
        <DetailRow label="Transaction hash">
          <AddressValue address={transaction.tx_hash} />
        </DetailRow>
        <DetailRow label="Network">
          <span className="inline-flex items-center gap-2 text-sm">
            <CryptoNetworkIcon
              network={transaction.network ?? ''}
              className="size-5"
            />
            {hasValue(transaction.network)
              ? getNetworkDisplayName(transaction.network)
              : '-'}
          </span>
        </DetailRow>
        <DetailRow label="Status">
          <StatusValue status={transaction.status} />
        </DetailRow>
      </div>
    </>
  );
};

const ExchangeDetails = ({
  transaction
}: {
  transaction: CryptoTransaction;
}) => {
  const displayDate = transaction.created_at ?? transaction.display_at;
  const amount =
    hasValue(transaction.formatted_from_amount) &&
    hasValue(transaction.from_currency)
      ? `-${transaction.formatted_from_amount} ${transaction.from_currency}`
      : '-';
  const received =
    hasValue(transaction.formatted_to_amount) &&
    hasValue(transaction.to_currency)
      ? `${transaction.formatted_to_amount} ${transaction.to_currency}`
      : '-';

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col items-center gap-4">
        <p className="text-xl">
          {transaction.from_currency} → {transaction.to_currency}
        </p>

        <div className="flex items-center gap-2">
          <div className="relative inline-flex">
            <CryptoFlag
              cryptoCode={transaction.from_currency ?? ''}
              className="size-8"
            />
            <CryptoNetworkIcon
              network={transaction.from_network ?? ''}
              className="absolute -bottom-0.5 -right-0.5 size-4"
            />
          </div>

          <span className="text-content-secondary">→</span>
          <CountryFlag
            className="!size-[32px]"
            countryCode={getCountryCode(transaction.to_currency ?? '')}
            svg
          />
        </div>

        {hasValue(displayDate) ? (
          <InlineDateTime value={displayDate} />
        ) : (
          <span>-</span>
        )}
      </div>

      <div className="flex flex-col gap-4 px-2">
        <p className="text-base font-bold text-content-primary">
          Transaction details
        </p>

        <DetailRow label="Amount">{amount}</DetailRow>
        <DetailRow label="Received">{received}</DetailRow>
        <DetailRow label="Exchange rate">
          {hasValue(transaction.rate) ? shiftDecimal(transaction.rate, 8) : '-'}
        </DetailRow>
        <DetailRow label="Network">
          <span className="inline-flex items-center gap-1 text-sm">
            <CryptoNetworkIcon
              network={transaction.from_network ?? ''}
              className="size-4"
            />
            {hasValue(transaction.from_network)
              ? capitalizeFirstLetter(transaction.from_network.toLowerCase())
              : '-'}
          </span>
        </DetailRow>
        <DetailRow label="Status">
          <StatusValue status={transaction.status} />
        </DetailRow>
      </div>
    </div>
  );
};

export const TransactionDetailModal = ({ transaction, onClose }: Props) => {
  return (
    <Modal
      title={null}
      open={!!transaction}
      onCancel={onClose}
      footer={null}
      width={400}
    >
      {transaction.type === 'deposit' && (
        <DepositDetails transaction={transaction} />
      )}
      {transaction.type === 'withdrawal' && (
        <WithdrawalDetails transaction={transaction} />
      )}
      {transaction.type === 'exchange' && (
        <ExchangeDetails transaction={transaction} />
      )}
    </Modal>
  );
};
